import { FC, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { capitalizeFirstLetter } from '../../../helpers/utils';
// import TextEditor from "../TextEditor/TextEditor";
import Checkboxes from '../Checkboxes/Checkboxes';
import ColorPicker from '../ColorPicker/ColorPicker';
import TextEditor from '../TextEditor';
import ReactQuill from 'react-quill';
// import ImageField from "../ImageField/ImageField";
// import TimePickerValue from "../TimePickerValue/TimePickerValue";

interface Props {
  name: string;
  label: string;
  type: string;
  autoFocus: boolean;
  defaultValue: any;
  register: any;
  setValue: any;
  errors: any;
  control: any;
  options?: any;
  disabled?: boolean;
  hidden?: any;
  resource?: any;
  slug?: any;
  helperText?: string;
  helpContent?: React.ReactNode;
}

const FormField: FC<Props> = ({
  name,
  label,
  type,
  autoFocus,
  defaultValue,
  register,
  setValue,
  errors,
  control,
  options,
  disabled,
  hidden,
  resource,
  slug,
  helperText,
  helpContent,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // const quillRef = useRef<ReactQuill | null>(null);
  // const [isUploading, setIsUploading] = useState(false);

  const renderField = () => {
    switch (type) {
      case 'textField':
        return (
          <TextField
            fullWidth
            size="small"
            autoFocus={autoFocus}
            defaultValue={defaultValue}
            placeholder={capitalizeFirstLetter(name)}
            {...register(name)}
            error={errors[name] ? true : false}
            disabled={disabled}
            hidden={hidden}
            helperText={helperText}
            FormHelperTextProps={{
              sx: {
                marginTop: '4px',
                fontSize: '12px',
              },
            }}
          />
        );
      case 'textarea':
        return (
          <TextField
            fullWidth
            size="small"
            autoFocus={autoFocus}
            defaultValue={defaultValue}
            placeholder={capitalizeFirstLetter(name)}
            {...register(name)}
            error={errors[name] ? true : false}
            disabled={disabled}
            hidden={hidden}
            helperText={helperText}
            multiline
            rows={4}
            FormHelperTextProps={{
              sx: {
                marginTop: '4px',
                fontSize: '12px',
              },
            }}
          />
        );
      case 'password':
        return (
          <TextField
            fullWidth
            size="small"
            type="password"
            autoFocus={autoFocus}
            placeholder={capitalizeFirstLetter(name)}
            defaultValue={defaultValue}
            {...register(name)}
            error={errors[name] ? true : false}
            disabled={disabled}
            hidden={hidden}
          />
        );
      case 'number':
        return (
          <TextField
            fullWidth
            size="small"
            type="number"
            defaultValue={defaultValue}
            placeholder={capitalizeFirstLetter(name)}
            {...register(name)}
            error={errors[name] ? true : false}
            inputProps={{ min: 0 }}
            disabled={disabled}
            hidden={hidden}
          />
        );
      case 'phoneNumber':
        return (
          <TextField
            fullWidth
            size="small"
            type="tel"
            defaultValue={defaultValue}
            placeholder={capitalizeFirstLetter(name)}
            {...register(name)}
            error={errors[name] ? true : false}
            disabled={disabled}
            hidden={hidden}
          />
        );
      case 'date':
        return (
          <TextField
            fullWidth
            size="small"
            type="date"
            defaultValue={defaultValue}
            placeholder={capitalizeFirstLetter(name)}
            {...register(name)}
            error={errors[name] ? true : false}
            disabled={disabled}
            hidden={hidden}
            sx={{
              '& input[type="date"]::-webkit-calendar-picker-indicator': {
                fontSize: '22px',
                color: 'white',
                filter: 'invert(1)',
              },
            }}
          />
        );
      case 'select':
        return options ? (
          <TextField
            select
            fullWidth
            size="small"
            autoFocus={autoFocus}
            defaultValue={defaultValue}
            {...register(name)}
            error={errors[name] ? true : false}
            disabled={disabled}
            hidden={hidden}
          >
            {options.map((option: any) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ) : null;
      case 'checkboxes':
        return options ? (
          <Checkboxes
            label={label}
            name={name}
            options={options}
            register={register}
            defaultValue={defaultValue}
          />
        ) : null;
      case 'checkbox':
        return (
          <Box sx={{ mt: -1 }}>
            <Checkbox
              sx={{ marginLeft: '-11px' }}
              defaultChecked={defaultValue}
              {...register(name)}
              disabled={disabled}
              hidden={hidden}
            />
          </Box>
        );
      case 'autocomplete':
        return (
          <Autocomplete
            defaultValue={defaultValue}
            onChange={(e: any, value: string) => setValue(name, value)}
            isOptionEqualToValue={(option: any, value: any) =>
              option.id === value.id
            }
            options={options}
            disabled={disabled}
            getOptionLabel={(option: any) => option.label}
            renderInput={(params) => <TextField {...params} size="small" />}
            hidden={hidden}
          />
        );
      case 'color':
        return (
          <ColorPicker
            defaultColor={defaultValue}
            handleChange={(color) => setValue(name, color)}
          />
        );
      // case 'textEditor':
      //   return (
      //     <Box key={name} style={{ height: 400 }}>
      //       <Typography>{label}</Typography>
      //       {isUploading && <LoadingDots />}
      //       <ReactQuill
      //         ref={(ref) => {
      //           if (ref) {
      //             quillRef.current = ref;
      //           }
      //         }}
      //         value={
      //           quillValue && quillValue !== ''
      //             ? quillValue
      //             : defaultValue
      //         }
      //         onChange={handleQuillChange}
      //         modules={modules}
      //         style={{ height: 300 }}
      //       />
      //     </Box>
      //   );
      // case 'editor':
      //   return (
      //     <TextEditor
      //       initialValue={defaultValue}
      //       control={control}
      //       name={name}
      //     />
      //   );
      // case 'time':
      //   return (
      //     <Box sx={{ mt: -1 }}>
      //       <TimePickerValue
      //         name={name}
      //         defaultValue={defaultValue}
      //         setValue={setValue}
      //         register={register}
      //       />
      //     </Box>
      //   );
      // case 'image':
      //   return (
      //     <ImageField
      //       label={label}
      //       name={name}
      //       value={defaultValue}
      //       setValue={setValue}
      //       register={register}
      //       resource={resource}
      //       slug={slug}
      //     />
      //   );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      {type !== 'checkboxes' && type !== 'image' && !hidden && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: '4px' }}
        >
          <Typography
            component={'label'}
            htmlFor={name}
            sx={{ display: 'inline-block' }}
          >
            {label}
          </Typography>
          {helpContent && (
            <>
              <Tooltip title="Click for help">
                <IconButton
                  size="small"
                  onClick={handleClick}
                  sx={{
                    padding: '2px',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                PaperProps={{
                  sx: {
                    maxWidth: 500,
                    p: 2,
                  },
                }}
              >
                {helpContent}
              </Popover>
            </>
          )}
        </Box>
      )}
      {!hidden && renderField()}
      <Typography color="error" sx={{ fontSize: 14 }}>
        {errors[name]?.message}
      </Typography>
    </Box>
  );
};

export default FormField;
